import { useMemo } from 'react';
import { useCapability } from './useCapabilities';
import { useCancelWindow } from './useCancelWindow';
import { CAPABILITIES } from '../capabilities/capabilities';

/**
 * Hook to determine if the current user can perform refund/modify actions on a booking item.
 *
 * Combines cancel window status with user capabilities to determine permissions.
 *
 * @param {Object} bookingItem - The booking item object
 * @returns {{
 *   canRefund: boolean,
 *   canModify: boolean,
 *   isStartInsideCancelWindow: boolean,
 *   refundDisabledReason: string|null,
 *   modifyDisabledReason: string|null
 * }}
 */
export function useBookingPermissions(bookingItem) {
    const { hasCapability } = useCapability();
    const { isStartInsideCancelWindow } = useCancelWindow(bookingItem);

    return useMemo(() => {
        // Check refund capabilities
        const hasRefundWithin = hasCapability(CAPABILITIES.ALLOW_REFUND_WITHIN_CANCEL_WINDOW);
        const hasRefundOutside = hasCapability(CAPABILITIES.ALLOW_REFUND_OUTSIDE_CANCEL_WINDOW);

        // Check modify capabilities
        const hasModifyWithin = hasCapability(CAPABILITIES.ALLOW_MODIFY_WITHIN_CANCEL_WINDOW);
        const hasModifyOutside = hasCapability(CAPABILITIES.ALLOW_MODIFY_OUTSIDE_CANCEL_WINDOW);

        // Determine if actions are allowed
        // Inside cancel window = activity is close/passed, needs "within" capability
        // Outside cancel window = activity is far away, needs "outside" capability
        const canRefund =
            (isStartInsideCancelWindow && hasRefundWithin) ||
            (!isStartInsideCancelWindow && hasRefundOutside);
        const canModify =
            (isStartInsideCancelWindow && hasModifyWithin) ||
            (!isStartInsideCancelWindow && hasModifyOutside);

        // Generate disabled reasons for tooltips
        let refundDisabledReason = null;
        if (!canRefund) {
            if (isStartInsideCancelWindow) {
                refundDisabledReason =
                    'You do not have permission to refund within the cancellation window';
            } else {
                refundDisabledReason =
                    'You do not have permission to refund outside the cancellation window';
            }
        }

        let modifyDisabledReason = null;
        if (!canModify) {
            if (isStartInsideCancelWindow) {
                modifyDisabledReason =
                    'You do not have permission to modify within the cancellation window';
            } else {
                modifyDisabledReason =
                    'You do not have permission to modify outside the cancellation window';
            }
        }

        return {
            canRefund,
            canModify,
            isStartInsideCancelWindow,
            refundDisabledReason,
            modifyDisabledReason
        };
    }, [hasCapability, isStartInsideCancelWindow]);
}
